import { MouseEvent, useEffect, useState } from "react";
import "@style/Admin.scss";

import { useNavigate, useLocation } from "react-router-dom";
import AdminTitle from "./AdminTitle";
import AdminDashboardTab from "./AdminDashboardTab";
import AdminCompaniesTab from "./AdminCompaniesTab";
import AdminTestTypesTab from "./AdminTestTypesTab";
import AdminRecentsColumnsTab from "./AdminRecentsColumnsTab";
import AdminSectionsTab from "./AdminSectionsTab";
import AdminSystemLog from "./AdminSystemLog";
import AdminBillingTab from "@acct/tabs/AdminBillingTab";
import AdminTranslationsTab from "./AdminTranslationsTab";
import AdminLanguagesTab from "./AdminLanguagesTab";
import AdminReportsTab from "./AdminReportsTab";
import HelpMenuTab from "./HelpMenuTab";
import AdminEmailTab from "./AdminEmailTab";
import AdminContentsTab from "./AdminContentsTab";
import AdminKpsiTab from "./kpsi/AdminKpsiTab";
import Enums from "@/enums";
import { readPermIdsFromSession } from "@/i18n/sessionUser";

interface AdminProps {
  [key: string]: unknown;
}

function isTranslatorOnlyUser(permIds: number[]) {
  return permIds.includes(Enums.ROLE_PERMS.Translator)
    && !permIds.includes(Enums.ROLE_PERMS.SystemAdmin);
}

/** KPSI-only operators: have KpsiAccess, are not system admin, and are not translator-locked. */
function isKpsiOnlyUser(permIds: number[]) {
  return permIds.includes(Enums.ROLE_PERMS.KpsiAccess)
    && !permIds.includes(Enums.ROLE_PERMS.SystemAdmin)
    && !permIds.includes(Enums.ROLE_PERMS.Translator);
}

function canShowKpsiTab(permIds: number[]) {
  return permIds.includes(Enums.ROLE_PERMS.SystemAdmin)
    || permIds.includes(Enums.ROLE_PERMS.KpsiAccess);
}

function hasAdminMenuPermission(permIds: number[], permissionId: number) {
  return permIds.includes(Enums.ROLE_PERMS.SystemAdmin)
    || permIds.includes(permissionId);
}

type AdminTabVisibility = {
  canShowDashboardTab: boolean;
  canShowCompaniesTab: boolean;
  canShowKpsiTab: boolean;
  visibleTabIds: string[];
  firstVisibleTabId: string | null;
};

function getAdminTabVisibility(permIds: number[]): AdminTabVisibility {
  const isSystemAdmin = permIds.includes(Enums.ROLE_PERMS.SystemAdmin);
  const hasTranslator = permIds.includes(Enums.ROLE_PERMS.Translator);
  const canTranslate = isSystemAdmin || hasTranslator;
  const canShowHelpTab = isSystemAdmin || permIds.includes(Enums.ROLE_PERMS.CompanyMenuHelp);
  const showKpsi = canShowKpsiTab(permIds);

  const canShowDashboardTab = hasAdminMenuPermission(permIds, Enums.ROLE_PERMS.CompanyMenuDashboard);
  const canShowCompaniesTab = hasAdminMenuPermission(permIds, Enums.ROLE_PERMS.CompanyMenuCompany);

  const tabEntries: Array<{ id: string; visible: boolean }> = [
    { id: "dashboard", visible: canShowDashboardTab },
    { id: "companies", visible: canShowCompaniesTab },
    { id: "testTypes", visible: isSystemAdmin },
    { id: "sections", visible: isSystemAdmin },
    { id: "recents", visible: isSystemAdmin },
    { id: "languages", visible: isSystemAdmin },
    { id: "translations", visible: canTranslate },
    { id: "systemLog", visible: isSystemAdmin },
    { id: "email", visible: isSystemAdmin },
    { id: "help", visible: canShowHelpTab },
    { id: "contents", visible: isSystemAdmin },
    { id: "accounting", visible: isSystemAdmin },
    { id: "reports", visible: isSystemAdmin },
    { id: "kpsi", visible: showKpsi },
  ];

  const visibleTabIds = tabEntries.filter((tab) => tab.visible).map((tab) => tab.id);

  return {
    canShowDashboardTab,
    canShowCompaniesTab,
    canShowKpsiTab: showKpsi,
    visibleTabIds,
    firstVisibleTabId: visibleTabIds[0] ?? null,
  };
}

export default function Admin(props: AdminProps) {
  let navigate = useNavigate();
  var location = useLocation();
  const initialPermIds = readPermIdsFromSession();
  const [activeTab, updateActiveTab] = useState("");
  const [colorScheme, updateColorScheme] = useState<any>(null);
  const [showSpinner, updateShowSpinner] = useState(false);
  const [adminPerms, updateAdminPerms] = useState<number[]>(initialPermIds);
  const isSystemAdmin = adminPerms.includes(Enums.ROLE_PERMS.SystemAdmin);
  const hasTranslator = adminPerms.includes(Enums.ROLE_PERMS.Translator);
  const canTranslate = isSystemAdmin || hasTranslator;
  const isTranslatorOnly = isTranslatorOnlyUser(adminPerms);
  const isKpsiOnly = isKpsiOnlyUser(adminPerms);
  const canShowHelpTab = isSystemAdmin || adminPerms.includes(Enums.ROLE_PERMS.CompanyMenuHelp);
  const {
    canShowDashboardTab,
    canShowCompaniesTab,
    canShowKpsiTab: showKpsiMenu,
    visibleTabIds,
  } = getAdminTabVisibility(adminPerms);

  useEffect(() => {
    if (!window.sessionStorage.getItem("BearerToken")) {
      navigate("../Login");
      return;
    }

    const uniquePermIds = readPermIdsFromSession();
    updateAdminPerms(uniquePermIds);

    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const translatorOnly = isTranslatorOnlyUser(uniquePermIds);
    const kpsiOnly = isKpsiOnlyUser(uniquePermIds);

    if (translatorOnly) {
      if (tab !== "translations") {
        updateActiveTab("translations");
        navigate("?tab=translations", { replace: true });
        return;
      }
      if (activeTab !== "translations") {
        updateActiveTab("translations");
      }
      return;
    }

    if (kpsiOnly) {
      if (tab !== "kpsi") {
        updateActiveTab("kpsi");
        navigate("?tab=kpsi", { replace: true });
        return;
      }
      if (activeTab !== "kpsi") {
        updateActiveTab("kpsi");
      }
      return;
    }

    const tabVisibility = getAdminTabVisibility(uniquePermIds);
    const requestedTab = tab ?? "";
    const resolvedTab = tabVisibility.visibleTabIds.includes(requestedTab)
      ? requestedTab
      : tabVisibility.firstVisibleTabId;

    if (!resolvedTab) {
      if (activeTab) {
        updateActiveTab("");
      }
      return;
    }

    if (!requestedTab || requestedTab !== resolvedTab) {
      navigate(`?tab=${resolvedTab}`, { replace: true });
    }

    if (activeTab !== resolvedTab) {
      updateActiveTab(resolvedTab);
    }
  }, [activeTab, location.search, navigate]);

  // useEffect(() => {
  //   console.log("Show spinner:", showSpinner);
  // }, [showSpinner]);

  useEffect(() => {
    if (colorScheme && colorScheme.schemeElements) {
      colorScheme.schemeElements.forEach((e) => {
        document.documentElement.style.setProperty(e.variable, e.colorValue);
      });
    }
  }, [colorScheme]);


  // async function getColorSchemeAsync() {
  //   updateSpinner(true);
  //   const resp = await RightTimeApi.getAsync("/ColorScheme?name=Admin");
  //   updateSpinner(false);
  //   if (resp && resp.ok && resp.jsonData) {
  //     updateColorScheme(resp.jsonData);
  //   }
  //   return resp;
  // }

  function onChangeTab(e: MouseEvent<HTMLSpanElement>) {
    const newTab = e.currentTarget.dataset.tab;
    console.log(`onChangeTab: newTab=${newTab}, currentActiveTab=${activeTab}`);
    if (newTab && newTab !== activeTab) {
      console.log(`Changing tab from ${activeTab} to ${newTab}`);
      updateActiveTab(newTab);
      navigate(`?tab=${newTab}`);
    } else {
      console.log("Not changing tab:", { newTab, activeTab, condition: newTab && newTab !== activeTab });
    }
  }

  let tabData = {
    //company: company,
    activeTab: activeTab,
    updateSpinner: updateShowSpinner,
    adminPerms: adminPerms
  };
  // console.log("Rendering Admin with tabData:", activeTab);
  return (
    <>
      {/* Overlay only renders when showSpinner is true */}
      {showSpinner && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      {/* Main content, completely separate from overlay */}
      <div className="adminPage">
        <AdminTitle
          title="Admin Interface"
          company={{ permissions: adminPerms.map(p => ({ value: p })) }}
          activeTab={activeTab}
          updateSpinner={updateShowSpinner}
          helpScope="admin"
        />
        <div className="pageContent">
          {!isTranslatorOnly && !isKpsiOnly && visibleTabIds.length > 0 && (
          <div className="tabHeader">
            {canShowDashboardTab && (
            <span
              className={activeTab === "dashboard" ? "tab active" : "tab"}
              data-tab="dashboard"
              onClick={onChangeTab}
            >
              Dashboard
            </span>
            )}
            {canShowCompaniesTab && (
            <span
              className={activeTab === "companies" ? "tab active" : "tab"}
              data-tab="companies"
              onClick={onChangeTab}
            >
              Companies
            </span>
            )}
            {isSystemAdmin && (
            <span
              className={activeTab === "testTypes" ? "tab active" : "tab"}
              data-tab="testTypes"
              onClick={onChangeTab}
            >
              Test Types
            </span>
            )}
            {isSystemAdmin && (
            <span
              className={activeTab === "sections" ? "tab active" : "tab"}
              data-tab="sections"
              onClick={onChangeTab}
            >
              Sections
            </span>
            )}
            {/* {isSystemAdmin && (
            <span
              className={activeTab === "colors" ? "tab active" : "tab"}
              name="colors"
              onClick={onChangeTab}
            >
              Color Schemes
            </span>
            )} */}
            { isSystemAdmin && (
            <span
              className={activeTab === "recents" ? "tab active" : "tab"}
              data-tab="recents"
              onClick={onChangeTab}
            >
              Recents Column Sets
            </span>
            )}
            { isSystemAdmin && (
            <span
              className={activeTab === "languages" ? "tab active" : "tab"}
              data-tab="languages"
              onClick={onChangeTab}
            >
              Languages
            </span>
            )}
            { canTranslate && (
            <span
              className={activeTab === "translations" ? "tab active" : "tab"}
              data-tab="translations"
              onClick={onChangeTab}
            >
              Translations
            </span>
            )}
            { isSystemAdmin && (
            <span
              className={activeTab === "systemLog" ? "tab active" : "tab"}
              data-tab="systemLog"
              onClick={onChangeTab}
            >
              System Log
            </span>
            )}
            { isSystemAdmin && (
            <span
              className={activeTab === "email" ? "tab active" : "tab"}
              data-tab="email"
              onClick={onChangeTab}
            >
              Email
            </span>
            )}
            { canShowHelpTab && (
            <span
              className={activeTab === "help" ? "tab active" : "tab"}
              data-tab="help"
              onClick={onChangeTab}
            >
              Help
            </span>
            )}
            { isSystemAdmin && (
            <span
              className={activeTab === "contents" ? "tab active" : "tab"}
              data-tab="contents"
              onClick={onChangeTab}
            >
              Contents
            </span>
            )}
            { isSystemAdmin && (
            <span
              className={activeTab === "accounting" ? "tab active" : "tab"}
              data-tab="accounting"
              onClick={onChangeTab}
            >
              Accounting
            </span>
            )}
            { isSystemAdmin && (
            <span
              className={activeTab === "reports" ? "tab active" : "tab"}
              data-tab="reports"
              onClick={onChangeTab}
            >
              Reports
            </span>
            )}
            { showKpsiMenu && (
            <span
              className={activeTab === "kpsi" ? "tab active" : "tab"}
              data-tab="kpsi"
              onClick={onChangeTab}
            >
              KPSI
            </span>
            )}
          </div>
          )}
          <div className="tabContent">
            {isTranslatorOnly ? (
              <AdminTranslationsTab tabData={tabData} />
            ) : isKpsiOnly ? (
              <AdminKpsiTab tabData={tabData} />
            ) : (
            <>
            <AdminDashboardTab tabData={tabData} />
            <AdminCompaniesTab tabData={tabData} />
            <AdminTestTypesTab tabData={tabData} />
            <AdminSectionsTab tabData={tabData} />
            <AdminRecentsColumnsTab tabData={tabData} />
            <AdminLanguagesTab tabData={tabData} />
            <AdminTranslationsTab tabData={tabData} />
            <AdminKpsiTab tabData={tabData} />
            <AdminSystemLog tabData={tabData} />
            <HelpMenuTab tabData={tabData} helpSection="system.help" isSystemAdmin={isSystemAdmin} />
            <AdminEmailTab tabData={tabData} />
            <AdminContentsTab tabData={tabData} />
            <AdminBillingTab tabData={tabData} />
            <AdminReportsTab tabData={tabData} />
            </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
